import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Package, Loader2, RefreshCw, Search,
  DollarSign, Truck, FileCheck, ChevronDown, FileImage, MessageCircle,
  Download, FileSpreadsheet, FileText, Trash2, Pencil, X, Save,
  Tag, ExternalLink, AlertCircle, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import AdminHeader from './AdminHeader';
import ArtworkDieline from './ArtworkDieline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('kaha_session');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Status badge colors
const statusColors = {
  // Payment
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
  refunded: 'bg-purple-100 text-purple-800',
  // Customer
  pendiente_pago: 'bg-amber-100 text-amber-800',
  pago_confirmado: 'bg-green-100 text-green-800',
  revision_archivo: 'bg-blue-100 text-blue-800',
  aprobado_produccion: 'bg-emerald-100 text-emerald-800',
  en_produccion: 'bg-indigo-100 text-indigo-800',
  terminado: 'bg-teal-100 text-teal-800',
  listo_entrega: 'bg-green-100 text-green-800',
  cancelado: 'bg-red-100 text-red-800',
  // Artwork
  not_uploaded: 'bg-gray-100 text-gray-600',
  uploaded: 'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  needs_changes: 'bg-orange-100 text-orange-800',
  manually_overridden: 'bg-purple-100 text-purple-800',
  // Production
  pendiente: 'bg-gray-100 text-gray-600',
  diseno_ajuste: 'bg-blue-100 text-blue-800',
  impresion: 'bg-indigo-100 text-indigo-800',
  suaje: 'bg-violet-100 text-violet-800',
  pegado: 'bg-pink-100 text-pink-800',
  completado: 'bg-green-100 text-green-800',
};

const Badge = ({ status, label }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-600'}`}>
    {label || status}
  </span>
);

const AdminWorkflow = ({ adminUser }) => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(null);

  const fetchData = useCallback(async () => {
    const headers = getAuthHeaders();
    // Load orders independently - public endpoint
    try {
      const ordersRes = await axios.get(`${API}/orders?limit=200`);
      setOrders(ordersRes.data);
    } catch (err) {
      console.warn('Orders fetch error:', err.message);
      toast.error('Error al cargar órdenes');
    }
    // Load admin stats
    try {
      const statsRes = await axios.get(`${API}/admin/stats`, { headers });
      setStats(statsRes.data);
    } catch (err) { console.warn('Stats load:', err.message); }
    // Load statuses
    try {
      const statusesRes = await axios.get(`${API}/admin/workflow/statuses`, { headers });
      setStatuses(statusesRes.data);
    } catch (err) { console.warn('Statuses load:', err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleStatusUpdate = async (orderId, updateData) => {
    setUpdating(orderId);
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, updateData, { headers: getAuthHeaders() });
      toast.success('Estado actualizado');
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
    setUpdating(null);
  };

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const res = await axios.get(`${API}/admin/orders/export/${type}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      link.download = `KAHA-Ordenes.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Archivo ${ext.toUpperCase()} descargado`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al exportar');
    }
    setExporting(null);
  };

  const handleDeleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`¿Seguro que deseas eliminar la orden ${orderNumber}? Esta acción no se puede deshacer.`)) return;
    setDeletingOrder(orderId);
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, { headers: getAuthHeaders() });
      toast.success(`Orden ${orderNumber} eliminada`);
      setExpandedOrder(null);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
    setDeletingOrder(null);
  };

  const handleEditOrder = async (orderId, editData) => {
    setUpdating(orderId);
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, editData, { headers: getAuthHeaders() });
      toast.success('Orden actualizada');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al editar');
    }
    setUpdating(null);
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = !searchTerm ||
      o.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.customer_email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || o.customer_status === filterStatus;
    const matchesPayment = filterPayment === 'all' || o.payment_status === filterPayment;
    return matchesSearch && matchesFilter && matchesPayment;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader adminUser={adminUser} currentPage="orders" />

      <main className="max-w-[1400px] mx-auto px-4 py-6">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            <StatCard label="Total Ordenes" value={stats.total_orders} />
            <StatCard label="Pendiente Pago" value={stats.pending_payment} color="amber" />
            <StatCard label="Pagadas" value={stats.paid} color="green" />
            <StatCard label="En Producción" value={stats.in_production} color="indigo" />
            <StatCard label="Completadas" value={stats.completed} color="teal" />
            <StatCard label="Ingreso Total" value={`$${stats.total_revenue?.toLocaleString('es-MX')}`} color="accent" />
          </div>
        )}

        {/* Filters & Export */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por folio, cliente o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-orders"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-48" data-testid="filter-status">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {statuses && Object.entries(statuses.customer_statuses).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPayment} onValueChange={setFilterPayment}>
            <SelectTrigger className="w-48" data-testid="filter-payment">
              <SelectValue placeholder="Filtrar por pago" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los pagos</SelectItem>
              <SelectItem value="pending">Pendiente</SelectItem>
              <SelectItem value="paid">Pagado</SelectItem>
              <SelectItem value="failed">Fallido</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => handleExport('excel')}
              disabled={!!exporting}
              className="gap-1.5 text-xs"
              data-testid="btn-export-excel"
            >
              {exporting === 'excel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 text-green-600" />}
              Excel
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => handleExport('pdf')}
              disabled={!!exporting}
              className="gap-1.5 text-xs"
              data-testid="btn-export-pdf"
            >
              {exporting === 'pdf' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 text-red-500" />}
              PDF
            </Button>
          </div>
        </div>

        {/* Orders Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Folio</th>
                  <th className="text-left p-3 font-medium">Cliente</th>
                  <th className="text-left p-3 font-medium">Fecha</th>
                  <th className="text-left p-3 font-medium">Material</th>
                  <th className="text-left p-3 font-medium">Qty</th>
                  <th className="text-left p-3 font-medium">Total</th>
                  <th className="text-left p-3 font-medium">Pago</th>
                  <th className="text-left p-3 font-medium">Archivo</th>
                  <th className="text-left p-3 font-medium">Estado</th>
                  <th className="text-left p-3 font-medium">Producción</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr><td colSpan={11} className="text-center p-8 text-muted-foreground">No hay ordenes</td></tr>
                ) : (
                  filteredOrders.map(order => (
                    <React.Fragment key={order.id}>
                      <tr
                        className="border-b hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                        data-testid={`order-row-${order.id}`}
                      >
                        <td className="p-3 font-mono font-bold text-xs">{order.order_number}</td>
                        <td className="p-3">
                          <div className="font-medium">{order.customer_name}</div>
                          <div className="text-xs text-muted-foreground">{order.customer_email}</div>
                        </td>
                        <td className="p-3 text-xs text-muted-foreground">{new Date(order.created_at).toLocaleDateString('es-MX')}</td>
                        <td className="p-3 text-xs">{order.material?.name || '-'}</td>
                        <td className="p-3">{order.quantity}</td>
                        <td className="p-3 font-medium">${order.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td className="p-3"><Badge status={order.payment_status} label={statuses?.payment_statuses?.[order.payment_status] || order.payment_status} /></td>
                        <td className="p-3"><Badge status={order.artwork_status} label={statuses?.artwork_statuses?.[order.artwork_status] || order.artwork_status} /></td>
                        <td className="p-3"><Badge status={order.customer_status} label={statuses?.customer_statuses?.[order.customer_status] || order.customer_status} /></td>
                        <td className="p-3"><Badge status={order.production_stage} label={statuses?.production_stages?.[order.production_stage] || order.production_stage} /></td>
                        <td className="p-3">
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandedOrder === order.id ? 'rotate-180' : ''}`} />
                        </td>
                      </tr>

                      {/* Expanded row with actions */}
                      {expandedOrder === order.id && (
                        <tr>
                          <td colSpan={11} className="p-4 bg-muted/20 border-b">
                            <OrderActions
                              order={order}
                              statuses={statuses}
                              onUpdate={(data) => handleStatusUpdate(order.id, data)}
                              onEdit={(data) => handleEditOrder(order.id, data)}
                              onDelete={() => handleDeleteOrder(order.id, order.order_number)}
                              onRefresh={fetchData}
                              updating={updating === order.id}
                              deleting={deletingOrder === order.id}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, color = 'default' }) => {
  const colors = {
    default: 'text-primary',
    amber: 'text-amber-600',
    green: 'text-green-600',
    indigo: 'text-indigo-600',
    teal: 'text-teal-600',
    accent: 'text-accent',
  };
  return (
    <Card className="p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-xl font-bold ${colors[color]}`}>{value}</p>
    </Card>
  );
};

const OrderActions = ({ order, statuses, onUpdate, onEdit, onDelete, onRefresh, updating, deleting }) => {
  const [note, setNote] = useState('');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    customer_name: order.customer_name || '',
    customer_email: order.customer_email || '',
    customer_phone: order.customer_phone || '',
    quantity: order.quantity || 1,
    total_amount: order.total_amount || 0,
    shipping_method: order.shipping_method || '',
    shipping_cost: order.shipping_cost || 0,
    shipping_carrier: order.shipping_carrier || '',
  });

  if (!statuses) return null;

  const customerOptions = Object.entries(statuses.customer_statuses).filter(([k]) => k !== order.customer_status);
  const productionOptions = Object.entries(statuses.production_stages).filter(([k]) => k !== order.production_stage);
  const artworkOptions = Object.entries(statuses.artwork_statuses).filter(([k]) => k !== order.artwork_status);

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Estado del pedido</label>
        <Select onValueChange={(val) => onUpdate({ customer_status: val, note, changed_by: 'admin' })} disabled={updating}>
          <SelectTrigger className="h-9" data-testid="select-customer-status">
            <SelectValue placeholder={statuses.customer_statuses[order.customer_status]} />
          </SelectTrigger>
          <SelectContent>
            {customerOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Etapa de producción</label>
        <Select onValueChange={(val) => onUpdate({ production_stage: val, note, changed_by: 'admin' })} disabled={updating}>
          <SelectTrigger className="h-9" data-testid="select-production-stage">
            <SelectValue placeholder={statuses.production_stages[order.production_stage]} />
          </SelectTrigger>
          <SelectContent>
            {productionOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Estado del archivo</label>
        <Select onValueChange={(val) => onUpdate({ artwork_status: val, note, changed_by: 'admin' })} disabled={updating}>
          <SelectTrigger className="h-9" data-testid="select-artwork-status">
            <SelectValue placeholder={statuses.artwork_statuses[order.artwork_status]} />
          </SelectTrigger>
          <SelectContent>
            {artworkOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-xs font-medium text-muted-foreground mb-1 block">Nota</label>
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Agregar nota..."
          className="h-9"
          data-testid="input-note"
        />
      </div>

      {/* Order details */}
      <div className="sm:col-span-2 lg:col-span-4 grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t text-xs">
        <div>
          <span className="text-muted-foreground">Dimensiones:</span>
          <p className="font-medium">{order.dimensions?.length} x {order.dimensions?.width} x {order.dimensions?.height} mm</p>
        </div>
        <div>
          <span className="text-muted-foreground">Impresion:</span>
          <p className="font-medium">
            {order.printing?.has_printing 
              ? `Si (${order.printing.coverage_percent}%) — ${order.printing?.print_mode === 'text' ? 'Texto' : 'Archivo'}`
              : 'No'}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground">Proveedor de pago:</span>
          <p className="font-medium">{order.payment_provider || '-'}</p>
        </div>
        <div className="flex flex-col justify-between">
          <div>
            <span className="text-muted-foreground">ID Transacción:</span>
            <p className="font-medium font-mono">{order.payment_transaction_id || '-'}</p>
          </div>
          {order.payment_status !== 'paid' && (
            <Button size="sm" variant="outline"
              className="h-7 text-[10px] gap-1 mt-2 border-green-300 text-green-700 hover:bg-green-50"
              onClick={async () => {
                try {
                  await axios.post(`${API}/admin/orders/${order.id}/mark-paid`, {}, { headers: getAuthHeaders() });
                  toast.success('Pago marcado como pagado');
                  onRefresh();
                } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
              }}
              data-testid="btn-mark-paid"
            >
              <DollarSign className="w-3 h-3" /> Marcar pagado
            </Button>
          )}
        </div>
      </div>

      {/* Production Sheet Info */}
      {order.dimensions?.length && order.dimensions?.width && order.dimensions?.height ? (
        <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t" data-testid="production-sheet-info">
          <p className="text-xs font-semibold text-primary mb-2 flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5" /> Produccion
          </p>
          <ProductionSheet order={order} />
        </div>
      ) : null}

      {/* Text Customization Details */}
      {order.text_customization?.text_content && (
        <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t" data-testid="admin-text-customization">
          <p className="text-xs font-medium text-muted-foreground mb-2">Texto personalizado del cliente</p>
          <div className="bg-muted/30 rounded-lg p-4 space-y-2">
            <p className="text-base font-bold" style={{
              color: order.text_customization.text_color || '#222',
              fontFamily: order.text_customization.font_family || 'Arial',
              textAlign: order.text_customization.text_alignment || 'center',
            }}>
              "{order.text_customization.text_content}"
            </p>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
              <span>Color: <span className="inline-block w-3 h-3 rounded-full align-middle border" style={{ backgroundColor: order.text_customization.text_color }} /> {order.text_customization.text_color}</span>
              <span>Tipografia: {order.text_customization.font_family}</span>
              <span>Tamaño: {order.text_customization.font_size}</span>
              <span>Alineacion: {order.text_customization.text_alignment}</span>
            </div>
          </div>
        </div>
      )}

      {/* Edit & Delete */}
      <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t flex flex-wrap gap-2">
        {!editing ? (
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5" onClick={() => setEditing(true)} data-testid="btn-edit-order">
            <Pencil className="w-3.5 h-3.5" /> Editar orden
          </Button>
        ) : (
          <div className="w-full space-y-3 p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold">Editando orden {order.order_number}</p>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditing(false)}><X className="w-3.5 h-3.5" /></Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-muted-foreground">Cliente</label>
                <Input className="h-8 text-xs" value={editForm.customer_name} onChange={e => setEditForm(p => ({...p, customer_name: e.target.value}))} data-testid="edit-customer-name" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Email</label>
                <Input className="h-8 text-xs" value={editForm.customer_email} onChange={e => setEditForm(p => ({...p, customer_email: e.target.value}))} data-testid="edit-customer-email" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Teléfono</label>
                <Input className="h-8 text-xs" value={editForm.customer_phone} onChange={e => setEditForm(p => ({...p, customer_phone: e.target.value}))} data-testid="edit-customer-phone" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Cantidad</label>
                <Input className="h-8 text-xs" type="number" value={editForm.quantity} onChange={e => setEditForm(p => ({...p, quantity: parseInt(e.target.value) || 0}))} data-testid="edit-quantity" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Total MXN</label>
                <Input className="h-8 text-xs" type="number" step="0.01" value={editForm.total_amount} onChange={e => setEditForm(p => ({...p, total_amount: parseFloat(e.target.value) || 0}))} data-testid="edit-total" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Metodo envio</label>
                <Input className="h-8 text-xs" placeholder="pickup / delivery" value={editForm.shipping_method} onChange={e => setEditForm(p => ({...p, shipping_method: e.target.value}))} data-testid="edit-shipping-method" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Costo envio</label>
                <Input className="h-8 text-xs" type="number" step="0.01" value={editForm.shipping_cost} onChange={e => setEditForm(p => ({...p, shipping_cost: parseFloat(e.target.value) || 0}))} data-testid="edit-shipping-cost" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground">Transportista</label>
                <Input className="h-8 text-xs" placeholder="fedex, dhl..." value={editForm.shipping_carrier} onChange={e => setEditForm(p => ({...p, shipping_carrier: e.target.value}))} data-testid="edit-shipping-carrier" />
              </div>
            </div>
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-accent hover:bg-accent/90" disabled={updating}
              onClick={() => { onEdit(editForm); setEditing(false); }} data-testid="btn-save-edit">
              {updating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar cambios
            </Button>
          </div>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
          onClick={onDelete} disabled={deleting} data-testid="btn-delete-order">
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Eliminar orden
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
          onClick={async () => {
            try {
              const res = await axios.get(`${API}/admin/orders/${order.id}/full`, { headers: getAuthHeaders() });
              const jsonStr = JSON.stringify(res.data, null, 2);
              const blob = new Blob(
                [`<html><head><title>JSON ${order.order_number || order.id}</title></head><body><pre style="font-size:12px;padding:20px;background:#1a1a1a;color:#0f0;margin:0;min-height:100vh">${jsonStr.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</pre></body></html>`],
                { type: 'text/html' }
              );
              window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
            } catch (err) { toast.error('Error al cargar datos'); }
          }}
          data-testid="btn-view-json">
          Ver JSON
        </Button>
      </div>

      {/* Shipping / Label Generation */}
      <ShippingSection order={order} />

      {/* WhatsApp Notification */}
      <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t">
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5 text-green-700 border-green-300 hover:bg-green-50"
          onClick={async (e) => {
            e.stopPropagation();
            try {
              const res = await axios.get(`${API}/admin/orders/${order.id}/whatsapp-url`, { headers: getAuthHeaders() });
              window.open(res.data.url, '_blank');
            } catch (err) {
              toast.error('Error al generar enlace de WhatsApp');
            }
          }}
          data-testid="btn-whatsapp-notify"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Notificar por WhatsApp
        </Button>
      </div>

      {/* Artwork & Dieline Section */}
      <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t">
        <ArtworkDieline orderId={order.id} />
      </div>
    </div>
  );
};


const ProductionSheet = ({ order }) => {
  const dims = order.dimensions || {};
  const L = parseFloat(dims.length) || 0;
  const W = parseFloat(dims.width) || 0;
  const H = parseFloat(dims.height) || 0;

  if (!L || !W || !H) return <p className="text-xs text-muted-foreground">No hay medidas suficientes para calcular la lamina</p>;

  const GLUE_FLAP = 35; // mm
  const MARGIN = 20; // mm
  const sheetWidthMm = 2 * L + 2 * W + GLUE_FLAP;
  const sheetHeightMm = H + W + MARGIN;
  const sheetWidthCm = (sheetWidthMm / 10).toFixed(1);
  const sheetHeightCm = (sheetHeightMm / 10).toFixed(1);
  const areaM2 = ((sheetWidthMm * sheetHeightMm) / 1_000_000).toFixed(4);
  const materialName = order.material?.name || 'Material no definido';
  const qty = order.quantity || 1;
  const boxCm = `${(L / 10).toFixed(1)} x ${(W / 10).toFixed(1)} x ${(H / 10).toFixed(1)} cm`;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs bg-muted/20 rounded-lg p-3">
      <div>
        <span className="text-muted-foreground block">Medida de caja</span>
        <span className="font-medium">{boxCm}</span>
      </div>
      <div>
        <span className="text-muted-foreground block">Material</span>
        <span className="font-medium">{materialName}</span>
      </div>
      <div>
        <span className="text-muted-foreground block">Medida de lamina</span>
        <span className="font-bold text-primary">{sheetWidthCm} x {sheetHeightCm} cm</span>
        <span className="text-[10px] text-muted-foreground block">{sheetWidthMm} x {sheetHeightMm} mm</span>
      </div>
      <div>
        <span className="text-muted-foreground block">Area de lamina</span>
        <span className="font-medium">{areaM2} m2</span>
      </div>
      <div>
        <span className="text-muted-foreground block">Cantidad</span>
        <span className="font-medium">{qty}</span>
      </div>
      <div>
        <span className="text-muted-foreground block">Laminas requeridas</span>
        <span className="font-bold text-primary">{qty}</span>
      </div>
    </div>
  );
};


const ShippingSection = ({ order }) => {
  const [loading, setLoading] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (order.shipping_method !== 'delivery') return;
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API}/admin/orders/${order.id}/shipping-info`, { headers: getAuthHeaders() });
        setShippingInfo(res.data);
      } catch (err) { console.warn('Shipping info fetch:', err.message); }
    };
    fetchInfo();
  }, [order.id, order.shipping_method]);

  if (order.shipping_method === 'pickup') {
    return (
      <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Truck className="w-4 h-4" />
          <span>Recoger en planta — sin guia de envio</span>
        </div>
      </div>
    );
  }

  if (!order.shipping_method) {
    return (
      <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t">
        <div className="flex items-center gap-2 text-xs text-amber-600">
          <AlertCircle className="w-4 h-4" />
          <span>Sin metodo de envio seleccionado</span>
        </div>
      </div>
    );
  }

  const info = shippingInfo || {};
  const hasTracking = !!info.shipping_tracking_number;
  const hasError = info.shipping_status === 'error';

  const handleGenerate = async (overrideCarrier) => {
    setGenerating(true);
    try {
      const body = overrideCarrier ? { carrier: overrideCarrier } : {};
      const res = await axios.post(`${API}/admin/orders/${order.id}/generate-shipment`, body, { headers: getAuthHeaders() });
      toast.success(`Guía generada: ${res.data.tracking_number}`);
      setShippingInfo(prev => ({
        ...prev,
        shipping_status: 'label_generated',
        shipping_tracking_number: res.data.tracking_number,
        shipping_label_url: res.data.label_url,
        shipping_track_url: res.data.track_url,
        shipping_error: null,
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar guía');
    }
    setGenerating(false);
  };

  return (
    <div className="sm:col-span-2 lg:col-span-4 pt-3 border-t space-y-3" data-testid="shipping-section">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-accent" />
        <span className="text-xs font-semibold">Envio — {(info.shipping_carrier || order.shipping_carrier || '').toUpperCase()}</span>
        {info.shipping_status && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
            info.shipping_status === 'label_generated' ? 'bg-green-100 text-green-800' :
            info.shipping_status === 'error' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-600'
          }`}>
            {info.shipping_status === 'label_generated' ? 'Guia generada' :
             info.shipping_status === 'error' ? 'Error' : info.shipping_status}
          </span>
        )}
      </div>

      {hasTracking ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-muted-foreground block">No. rastreo</span>
              <span className="font-mono font-bold">{info.shipping_tracking_number}</span>
            </div>
            <div>
              <span className="text-muted-foreground block">Transportista</span>
              <span className="font-medium capitalize">{info.shipping_carrier}</span>
            </div>
            <div>
              <span className="text-muted-foreground block">Costo</span>
              <span className="font-medium">${info.shipping_cost?.toLocaleString('es-MX', {minimumFractionDigits:2})} MXN</span>
            </div>
            <div>
              <span className="text-muted-foreground block">Generada</span>
              <span>{info.shipping_label_generated_at?.slice(0,10) || '-'}</span>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            {info.shipping_label_url && (
              <a href={info.shipping_label_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
                data-testid="btn-download-label">
                <Download className="w-3.5 h-3.5" /> Descargar guia PDF
              </a>
            )}
            {info.shipping_track_url && (
              <a href={info.shipping_track_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline"
                data-testid="btn-track-shipment">
                <ExternalLink className="w-3.5 h-3.5" /> Rastrear envio
              </a>
            )}
            <Button size="sm" variant="outline"
              className="h-7 text-[10px] gap-1 border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                if (!window.confirm('¿Regenerar guía? Solo funciona si la guía anterior fue cancelada en Envia.')) return;
                handleGenerate();
              }} disabled={generating}
              data-testid="btn-regenerate-label">
              {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Regenerar guía
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {hasError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
              <p className="font-semibold">Error al generar guía:</p>
              <p className="mt-1">{info.shipping_error}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {['fedex', 'estafeta', 'paquetexpress'].map(c => (
              <Button key={c} size="sm" variant="outline"
                className="h-8 text-xs gap-1.5 border-accent/40 text-accent hover:bg-accent/5"
                onClick={() => handleGenerate(c)} disabled={generating}
                data-testid={`btn-generate-${c}`}>
                {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Package Breakdown */}
      {info?.package_breakdown && (
        <div className="bg-muted/20 border border-border/40 rounded-lg p-3 text-xs space-y-2" data-testid="package-breakdown">
          <p className="font-semibold text-primary">Paquetes de envío</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-muted-foreground">
            <div><span className="block">Formato</span><span className="font-medium text-primary">{info.package_breakdown.format}</span></div>
            <div><span className="block">Cajas por paquete</span><span className="font-medium text-primary">{info.package_breakdown.boxes_per_bundle}</span></div>
            <div><span className="block">Total paquetes</span><span className="font-medium text-primary">{info.package_breakdown.total_bundles}</span></div>
            <div><span className="block">Espesor material</span><span className="font-medium text-primary">{info.package_breakdown.material_thickness_mm}mm ({info.package_breakdown.material_name})</span></div>
          </div>
          {info.package_breakdown.packages?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-border/30">
              <p className="text-muted-foreground mb-1">Medidas por paquete:</p>
              {info.package_breakdown.packages.slice(0, 3).map(p => (
                <p key={p.number} className="font-mono text-primary">
                  #{p.number}: <span className="font-bold">{p.length_cm} × {p.width_cm} × {p.height_cm} cm</span> — {p.weight_kg}kg ({p.boxes} cajas)
                </p>
              ))}
              {info.package_breakdown.packages.length > 3 && (
                <p className="text-muted-foreground">+{info.package_breakdown.packages.length - 3} paquetes más...</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tracking History Timeline */}
      {info?.tracking_history?.length > 0 && (
        <div className="bg-muted/20 border border-border/40 rounded-lg p-3 text-xs space-y-2" data-testid="tracking-timeline">
          <p className="font-semibold text-primary">Historial de rastreo</p>
          <div className="space-y-1.5 pl-3 border-l-2 border-accent/30">
            {info.tracking_history.slice().reverse().map((evt, i) => (
              <div key={evt.timestamp || `evt-${i}`} className="relative">
                <div className="absolute -left-[11px] top-1 w-2 h-2 rounded-full bg-accent" />
                <div className="pl-3">
                  <span className="font-semibold text-primary">{evt.status_label || evt.status}</span>
                  <span className="text-muted-foreground ml-2">
                    {evt.timestamp ? new Date(evt.timestamp).toLocaleString('es-MX', {day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : ''}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWorkflow;
